import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { KpiCard, fmtMoney, fmtDate, EmptyState, SkeletonRow, corporateInput } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

const CATEGORIES = [
  "Marketing", "Software", "Salarios", "Oficina", "Impuestos", "Servicios", "Otro",
];

export default function Accounting() {
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ description: "", amount: "", category: "Software", date: today, notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/reports/accounting-summary"),
        api.get("/expenses"),
      ]);
      setSummary(s.data);
      setExpenses(e.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Gasto registrado");
      setOpen(false);
      setForm({ description: "", amount: "", category: "Software", date: today, notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este gasto?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PageShell
      section={t("nav.group.finance")}
      page={t("acc.title")}
      context={summary ? `${lang === "en" ? "Balance" : "Balance"} ${fmtMoney(summary.balance)}` : ""}
      testid="accounting-toolbar"
      actions={
        <Button onClick={() => setOpen(true)} data-testid="add-expense-button" className="bg-slate-900 hover:bg-slate-800 text-white h-9">
          <Plus size={15} className="mr-1.5" /> {lang === "en" ? "Add expense" : "Registrar gasto"}
        </Button>
      }
    >

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-12">
        <KpiCard testid="acc-income" label={lang === "en" ? "Total income" : "Ingresos totales"} value={loading ? "—" : fmtMoney(summary?.total_income)} hint={lang === "en" ? "Paid invoices" : "Facturas pagadas"} accent="green" />
        <KpiCard testid="acc-expenses" label={lang === "en" ? "Total expenses" : "Gastos totales"} value={loading ? "—" : fmtMoney(summary?.total_expenses)} hint={lang === "en" ? "Recorded expenses" : "Gastos registrados"} accent="orange" />
        <KpiCard testid="acc-balance" label={lang === "en" ? "Net balance" : "Balance neto"} value={loading ? "—" : fmtMoney(summary?.balance)} hint={lang === "en" ? "Income − Expenses" : "Ingresos − Gastos"} accent={summary?.balance >= 0 ? "green" : "red"} />
      </div>

      <h2 className="text-xl font-medium text-slate-900 dark:text-white mb-4">{lang === "en" ? "Recent expenses" : "Gastos recientes"}</h2>
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Fecha</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Descripción</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">Categoría</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 text-right">Monto</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
            {!loading && expenses.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState
                  icon={Coins}
                  title="Sin gastos registrados"
                  message="Lleva control de tus egresos (marketing, software, salarios) para calcular tu balance neto correctamente."
                  action={
                    <Button onClick={() => setOpen(true)} data-testid="empty-add-expense" className="bg-slate-900 hover:bg-slate-800 text-white h-9">
                      <Plus size={15} className="mr-1.5" /> Registrar gasto
                    </Button>
                  }
                />
              </td></tr>
            )}
            {expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-4 text-slate-600">{fmtDate(e.date)}</td>
                <td className="px-5 py-4 text-slate-900 font-medium">{e.description}</td>
                <td className="px-5 py-4 text-slate-600">
                  <span className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">{e.category}</span>
                </td>
                <td className="px-5 py-4 text-right font-mono text-orange-600 font-semibold">{fmtMoney(e.amount)}</td>
                <td className="px-5 py-4 text-right">
                  <button onClick={() => handleDelete(e.id)} data-testid={`delete-expense-${e.id}`} className="text-slate-500 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors">
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-form-dialog">
          <DialogHeader><DialogTitle>Registrar gasto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Descripción *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc-input" className={corporateInput} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Monto (USD) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" className={corporateInput} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input" className={corporateInput} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select" className="h-11 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes-input" className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.description || !form.amount} data-testid="save-expense-button" className="bg-slate-900 hover:bg-slate-800 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
